import CalendarIcon from "@atlaskit/icon/core/calendar";
import ChevronDownIcon from "@atlaskit/icon/core/chevron-down";
import {DatePicker} from "@mantine/dates";
import {DateTime} from "luxon";
import {useCallback, useRef, useState, type CSSProperties} from "react";
import {AnankeButton} from "@vesta/ananke/components/anankeButton";
import {AnankePopup} from "@vesta/ananke/components/anankePopup";
import type {Iso8601DateTime} from "@vesta/common-type-definitions/typeDefinitions";
import {
    formatCalendarDateIsoSegment,
    parseCalendarDateFromIsoString,
} from "~/utilities/calendarDateIso";

type PickerDateValue = Date | string | null;
type PickerRange = [Date | null, Date | null];

export type DateRangeQuickPreset =
    | "today"
    | "yesterday"
    | "thisWeek"
    | "lastWeek"
    | "thisMonth"
    | "lastMonth"
    | "thisYear"
    | "allTime";

export type DateRangeQuickPresetDefinition = {
    label: string;
    value: string;
    getDates: (now: DateTime) => [Date, Date];
};

const ALL_TIME_START_DATE = "1970-01-01T00:00:00Z" as Iso8601DateTime;

export function getDateRangeQuickPresetDates(
    preset: DateRangeQuickPreset,
    now: DateTime = DateTime.local(),
): [Date, Date] {
    switch (preset) {
        case "today":
            return [now.startOf("day").toJSDate(), now.startOf("day").toJSDate()];
        case "yesterday": {
            const yesterday = now.minus({days: 1}).startOf("day").toJSDate();
            return [yesterday, yesterday];
        }
        case "thisWeek":
            return [now.startOf("week").startOf("day").toJSDate(), now.startOf("day").toJSDate()];
        case "lastWeek": {
            const lastWeek = now.minus({weeks: 1});
            return [
                lastWeek.startOf("week").startOf("day").toJSDate(),
                lastWeek.endOf("week").startOf("day").toJSDate(),
            ];
        }
        case "thisMonth":
            return [now.startOf("month").startOf("day").toJSDate(), now.startOf("day").toJSDate()];
        case "lastMonth": {
            const lastMonth = now.minus({months: 1});
            return [
                lastMonth.startOf("month").startOf("day").toJSDate(),
                lastMonth.endOf("month").startOf("day").toJSDate(),
            ];
        }
        case "thisYear":
            return [now.startOf("year").startOf("day").toJSDate(), now.startOf("day").toJSDate()];
        case "allTime":
            return [DateTime.local(1970, 1, 1).startOf("day").toJSDate(), now.startOf("day").toJSDate()];
    }
}

const DATE_RANGE_QUICK_PRESETS: ReadonlyArray<DateRangeQuickPresetDefinition> = [
    {label: "Today", value: "today", getDates: (now) => getDateRangeQuickPresetDates("today", now)},
    {label: "Yesterday", value: "yesterday", getDates: (now) => getDateRangeQuickPresetDates("yesterday", now)},
    {label: "This Week", value: "thisWeek", getDates: (now) => getDateRangeQuickPresetDates("thisWeek", now)},
    {label: "Last Week", value: "lastWeek", getDates: (now) => getDateRangeQuickPresetDates("lastWeek", now)},
    {label: "This Month", value: "thisMonth", getDates: (now) => getDateRangeQuickPresetDates("thisMonth", now)},
    {label: "Last Month", value: "lastMonth", getDates: (now) => getDateRangeQuickPresetDates("lastMonth", now)},
    {label: "This Year", value: "thisYear", getDates: (now) => getDateRangeQuickPresetDates("thisYear", now)},
    // {label: "All Time", value: "allTime", getDates: (now) => getDateRangeQuickPresetDates("allTime", now)},
];

export function dateRangePickerValueToIso8601DateTime(value: Date | string | null): Iso8601DateTime | null {
    if (value === null) {
        return null;
    }

    if (typeof value === "string") {
        const cal = parseCalendarDateFromIsoString(value);
        if (cal === null) {
            return null;
        }
        return `${cal.toFormat("yyyy-LL-dd")}T00:00:00Z` as Iso8601DateTime;
    }

    const dt = DateTime.fromJSDate(value);
    if (dt.isValid === false) {
        return null;
    }

    return `${dt.toFormat("yyyy-LL-dd")}T00:00:00Z` as Iso8601DateTime;
}

export function iso8601DateTimeToDateRangePickerValue(value: Iso8601DateTime | null): Date | null {
    if (value === null) {
        return null;
    }
    const dt = parseCalendarDateFromIsoString(value);
    if (dt === null) {
        return null;
    }
    return new Date(dt.year, dt.month - 1, dt.day);
}

/** Mantine v9 range picker emits `YYYY-MM-DD` strings; normalize before compare/apply. */
function toPickerDate(value: PickerDateValue): Date | null {
    if (value === null) {
        return null;
    }

    if (value instanceof Date) {
        if (Number.isNaN(value.getTime()) === true) {
            return null;
        }
        return value;
    }

    const fromCalendar = parseCalendarDateFromIsoString(value);
    if (fromCalendar !== null) {
        return new Date(fromCalendar.year, fromCalendar.month - 1, fromCalendar.day);
    }

    const fromFormat = DateTime.fromFormat(value, "yyyy-MM-dd");
    if (fromFormat.isValid === true) {
        return new Date(fromFormat.year, fromFormat.month - 1, fromFormat.day);
    }

    return null;
}

function normalizePickerRange(next: [PickerDateValue, PickerDateValue]): PickerRange {
    return [toPickerDate(next[0]), toPickerDate(next[1])];
}

export const DATE_RANGE_PILL_EMPTY_LABEL = "Date Range";

function isSameCalendarDay(left: Date, right: Date): boolean {
    return (
        left.getFullYear() === right.getFullYear() &&
        left.getMonth() === right.getMonth() &&
        left.getDate() === right.getDate()
    );
}

function isAllTimeRange(value: [Iso8601DateTime | null, Iso8601DateTime | null]): boolean {
    return value[0] === ALL_TIME_START_DATE && value[1] !== null;
}

function isoRangeToPickerRange(value: [Iso8601DateTime | null, Iso8601DateTime | null]): PickerRange {
    return [
        iso8601DateTimeToDateRangePickerValue(value[0]),
        iso8601DateTimeToDateRangePickerValue(value[1]),
    ];
}

export function formatDateRangePillLabel(
    start: Iso8601DateTime | null,
    end: Iso8601DateTime | null,
    emptyLabel: string = DATE_RANGE_PILL_EMPTY_LABEL,
): string {
    if (start === null && end === null) {
        return emptyLabel;
    }
    const fmt = (iso: string, includeYear: boolean) => formatCalendarDateIsoSegment(iso, includeYear);
    if (start !== null && end !== null) {
        const startDt = parseCalendarDateFromIsoString(start);
        const endDt = parseCalendarDateFromIsoString(end);
        if (startDt === null || endDt === null) {
            return DATE_RANGE_PILL_EMPTY_LABEL;
        }
        if (startDt.hasSame(endDt, "day")) {
            return fmt(start, true);
        }
        const sameYear = startDt.year === endDt.year;
        return `${fmt(start, !sameYear)} - ${fmt(end, true)}`;
    }
    return fmt(start !== null ? start : end !== null ? end : "", true);
}

export function DateRangePill({
    value,
    onChange,
    matchFilterPills = false,
    shouldRenderToParent = true,
    emptyLabel = DATE_RANGE_PILL_EMPTY_LABEL,
    isSelected,
    onClear,
    minDate,
    maxDate,
    showQuickPresets = false,
    quickPresetLayout = "responsive",
}: {
    value: [Iso8601DateTime | null, Iso8601DateTime | null];
    onChange: (range: [Iso8601DateTime | null, Iso8601DateTime | null]) => void;
    matchFilterPills?: boolean;
    /** When false, popup portals to document body (better inside scrollable filter rows). */
    shouldRenderToParent?: boolean;
    emptyLabel?: string;
    isSelected?: boolean;
    onClear?: () => void;
    /** When set, caps the earliest selectable date. */
    minDate?: Date;
    /** When set, caps the latest selectable date. Omit to allow future dates. */
    maxDate?: Date;
    /** Shows common calendar ranges above the date picker. */
    showQuickPresets?: boolean;
    /**
     * `responsive` follows the viewport: 3-column grid on small screens, sidebar from `sm` up.
     * `stacked` and `sidebar` pin one of those layouts regardless of viewport.
     */
    quickPresetLayout?: "responsive" | "stacked" | "sidebar";
}) {
    const [opened, setOpened] = useState(false);
    const [pickerValue, setPickerValue] = useState<PickerRange>([null, null]);
    const awaitingSameDayConfirmRef = useRef(false);

    const allTimeSelected = isAllTimeRange(value);
    const appliedLabel = allTimeSelected ? "All Time" : formatDateRangePillLabel(value[0], value[1], emptyLabel);
    const pickerLabelStart = dateRangePickerValueToIso8601DateTime(pickerValue[0]);
    const pickerLabelEnd = dateRangePickerValueToIso8601DateTime(pickerValue[1]);
    const label =
        opened === true && allTimeSelected === false
            ? formatDateRangePillLabel(pickerLabelStart, pickerLabelEnd, emptyLabel)
            : appliedLabel;
    const triggerIsSelected = isSelected ?? (value[0] !== null && value[1] !== null);
    // Pinned layouts use inline grid templates so they survive environments (Storybook)
    // where the responsive utilities are not emitted.
    const pinnedPanelStyle: CSSProperties | undefined =
        quickPresetLayout === "sidebar" ? {gridTemplateColumns: "9rem auto"} : undefined;
    const pinnedPresetsStyle: CSSProperties | undefined =
        quickPresetLayout === "stacked" ? {gridTemplateColumns: "repeat(3, minmax(0, 1fr))"} : undefined;

    const closePicker = useCallback(() => {
        setOpened(false);
        awaitingSameDayConfirmRef.current = false;
    }, []);

    const openPicker = useCallback(() => {
        setPickerValue(allTimeSelected ? [null, null] : isoRangeToPickerRange(value));
        awaitingSameDayConfirmRef.current = false;
        setOpened(true);
    }, [allTimeSelected, value]);

    const applyRange = useCallback(
        (start: Date, end: Date) => {
            const isoStart = dateRangePickerValueToIso8601DateTime(start);
            const isoEnd = dateRangePickerValueToIso8601DateTime(end);
            if (isoStart === null || isoEnd === null) {
                return;
            }

            if (isSameCalendarDay(start, end) === true) {
                onChange([isoStart, isoStart]);
            } else {
                onChange([isoStart, isoEnd]);
            }
            closePicker();
        },
        [closePicker, onChange],
    );

    const handlePickerChange = useCallback(
        (raw: [PickerDateValue, PickerDateValue]) => {
            const next = normalizePickerRange(raw);
            const [start, end] = next;
            setPickerValue(next);

            if (start === null) {
                awaitingSameDayConfirmRef.current = false;
                return;
            }

            if (end === null) {
                awaitingSameDayConfirmRef.current = true;
                return;
            }

            if (isSameCalendarDay(start, end) === true) {
                if (awaitingSameDayConfirmRef.current !== true) {
                    awaitingSameDayConfirmRef.current = true;
                    return;
                }
                applyRange(start, end);
                return;
            }

            awaitingSameDayConfirmRef.current = false;

            applyRange(start, end);
        },
        [applyRange],
    );

    return (
        <AnankePopup
            shouldRenderToParent={shouldRenderToParent}
            isOpen={opened}
            onClose={closePicker}
            trigger={(triggerProps) => (
                <AnankeButton
                    type="button"
                    {...triggerProps}
                    isSelected={triggerIsSelected}
                    onClick={() => {
                        if (opened === true) {
                            closePicker();
                            return;
                        }
                        openPicker();
                    }}
                >
                    {matchFilterPills ? (
                        <div className="grid grid-flow-col auto-cols-max gap-x-[0.375rem] items-center whitespace-nowrap">
                            <span>{label}</span>
                            <ChevronDownIcon label="" size="small" />
                        </div>
                    ) : (
                        <div className="grid grid-flow-col auto-cols-max gap-x-[0.375rem] items-center whitespace-nowrap">
                            <CalendarIcon label="" />
                            <span>{label}</span>
                            <ChevronDownIcon label="" />
                        </div>
                    )}
                </AnankeButton>
            )}
            content={() => (
                <div
                    className={
                        showQuickPresets === true
                            ? quickPresetLayout === "responsive"
                                ? "grid grid-cols-1 items-start sm:grid-cols-[9rem_auto]"
                                : "grid items-start"
                            : "p-ds-space-150"
                    }
                    style={pinnedPanelStyle}
                >
                    {showQuickPresets === true ? (
                        <div
                            className={
                                quickPresetLayout === "responsive"
                                    ? "grid grid-cols-3 gap-x-ds-space-100 gap-y-ds-space-075 border-b border-ds-border px-ds-space-100 py-ds-space-100 sm:min-h-full sm:grid-cols-1 sm:gap-x-0 sm:gap-y-0 sm:border-r sm:border-b-0 sm:py-ds-space-150"
                                    : quickPresetLayout === "stacked"
                                      ? "grid gap-x-ds-space-100 gap-y-ds-space-075 border-b border-ds-border px-ds-space-100 py-ds-space-100"
                                      : "grid min-h-full grid-cols-1 border-r border-ds-border px-ds-space-100 py-ds-space-150"
                            }
                            style={pinnedPresetsStyle}
                        >
                            {DATE_RANGE_QUICK_PRESETS.map((preset) => (
                                <button
                                    key={preset.value}
                                    type="button"
                                    className={
                                        quickPresetLayout === "stacked"
                                            ? "w-full rounded px-ds-space-050 py-ds-space-050 text-left text-ds-text hover:bg-ds-surface-hovered active:bg-ds-surface-pressed"
                                            : "w-full rounded px-ds-space-050 py-ds-space-050 text-left text-ds-text hover:bg-ds-surface-hovered active:bg-ds-surface-pressed sm:px-ds-space-100 sm:py-ds-space-100"
                                    }
                                    onClick={() => {
                                        const [start, end] = preset.getDates(DateTime.local());
                                        applyRange(start, end);
                                    }}
                                >
                                    {preset.label}
                                </button>
                            ))}
                        </div>
                    ) : null}
                    <div className={showQuickPresets === true ? "min-w-0 p-ds-space-150" : undefined}>
                        <DatePicker
                            type="range"
                            allowSingleDateInRange
                            value={pickerValue}
                            minDate={minDate}
                            maxDate={maxDate}
                            defaultDate={
                                pickerValue[0] ??
                                pickerValue[1] ??
                                maxDate ??
                                minDate ??
                                new Date()
                            }
                            onChange={(next) => {
                                handlePickerChange([next[0] ?? null, next[1] ?? null]);
                            }}
                        />
                        {onClear !== undefined ? (
                            <div className="mt-ds-space-100 flex justify-end">
                                <AnankeButton
                                    type="button"
                                    appearance="subtle"
                                    onClick={() => {
                                        onClear();
                                        closePicker();
                                    }}
                                >
                                    Clear
                                </AnankeButton>
                            </div>
                        ) : null}
                    </div>
                </div>
            )}
            placement="bottom-start"
        />
    );
}
